<script setup lang="ts">
import { tv } from 'tailwind-variants'

const packet = inject(keys.currentPacket)

// FIXME: The card padding styling currently put here is perfect for container queries. Currently, container queries doesn't work with CSS subgrid in Chromium/Safari, so we can't do that yet, but we should consider it in the future.
const variants = tv({
  slots: {
    wrapper: '',
    card: 'px-s py-legacy-m',
    grid: '',
    heading: '',
    content: '',
    titleSection: '',
    actionsSection: '',
  },
  variants: {
    theme: {
      'green-seagreen': {
        wrapper: 'bg-seagreen-40',
        grid: '[--preferred-col-width:275px]',
        heading: 'text-seagreen-5',
      },
      'dark-red-pink': {
        wrapper: 'bg-coral-60',
        grid: '[--preferred-col-width:100%]',
        heading: 'text-coral-pop',
        card: 'lg:pb-legacy-xl',
        actionsSection: 'lg:flex lg:items-center',
      },
      'green-red': {
        wrapper: 'bg-green-50',
        grid: '[--preferred-col-width:275px]',
        heading: 'text-green-pop',
      },
      'blue-pink': {
        wrapper: 'bg-blue-60',
        grid: '[--preferred-col-width:100%]',
        heading: 'text-coral-legacy-20',
      },
    },
    index: {
      0: '',
      1: '',
      2: '',
    },
  },

  compoundVariants: [
    {
      theme: ['green-seagreen', 'green-red'],
      class: {
        card: 'lg:px-m lg:py-legacy-l',
        content: 'grid grid-rows-subgrid',
        titleSection: 'border-b border-gray-20 pb-legacy-s',
        actionsSection: 'border-t border-gray-20 pt-legacy-s',
      },
    },
    {
      theme: ['dark-red-pink', 'blue-pink'],
      class: {
        card: 'lg:p-legacy-l',
        content: 'lg:flex lg:items-center lg:*:basis-1/2',
        actionsSection: 'mt-legacy-s border-t border-gray-20 pt-legacy-s lg:mt-0 lg:ml-legacy-m lg:size-full lg:border-t-0 lg:border-l lg:pt-0 lg:pl-legacy-m',
      },
    },
    {
      theme: ['green-seagreen', 'green-red'],
      index: 0,
      class: {
        card: 'rounded-br-[60px]',
      },
    },
    {
      theme: ['dark-red-pink'],
      index: 0,
      class: {
        card: 'rounded-tr-[60px]',
      },
    },
    {
      theme: ['green-seagreen', 'green-red'],
      index: 1,
      class: {
        card: 'rounded-tl-[60px]',
      },
    },
    {
      theme: ['green-seagreen', 'green-red'],
      index: 2,
      class: {
        card: 'rounded-tr-[60px]',
      },
    },
  ],
})

const classes = computed(() => variants({ theme: unref(packet)?.theme }))

// TODO: Consider performing this grouping on the mapper level (i.e. in #shared/utils/packet.ts)
const groupedVariants = computed(() => {
  const all = unref(packet)?.variants ?? []
  const relatedOrPickAndMatch: PacketVariant[] = []
  const regular: PacketVariant[] = []
  const relatedSkus = new Set<string>()

  for (const variant of all) {
    if (variant.isTrial)
      continue

    if (variant.included) {
      // At this point, we can't be sure if the included variant is related to another variant or not
      relatedOrPickAndMatch.push(variant)
    } else {
      regular.push(variant)
      variant.relatedSubscription.forEach((sku) => relatedSkus.add(sku))
    }
  }

  const pickAndMatch: PacketVariant[] = []
  const related: Record<string, PacketVariant> = {}

  for (const variant of relatedOrPickAndMatch) {
    if (relatedSkus.has(variant.sku)) {
      related[variant.sku] = variant
    } else {
      pickAndMatch.push(variant)
    }
  }

  return { pickAndMatch, regular, related }
})

const auth = useAuthorizeStore()
</script>

<template>
  <UContainer
    v-if="groupedVariants.regular.length > 0 || groupedVariants.pickAndMatch.length > 0"
    as="section"
    class="pt-legacy-l pb-legacy-xl md:pb-20"
    :class="classes.wrapper()"
  >
    <div class="mb-legacy-l flex flex-col gap-legacy-2xs text-center md:mb-legacy-xl" :class="classes.heading()">
      <h2 class="font-headline text-2xl md:text-5xl">
        Bestill abonnement
      </h2>
      <p class="text-base md:text-lg">
        Abonnementene varer i et skoleår og fornyes en gang i året.
      </p>
      <UiCampaignCallout
        v-if="packet?.campaign"
        class="mx-auto mt-legacy-m md:mt-legacy-l"
        :campaign="packet?.campaign"
      />
    </div>
    <ul
      data-cards
      class="mx-auto grid w-full gap-x-legacy-m gap-y-legacy-s"
      :class="classes.grid()"
    >
      <li
        v-for="(variant, i) in groupedVariants.regular"
        :key="variant.sku"
        class="z-0 row-span-4 grid grid-rows-subgrid"
      >
        <PacketVariantCardContainer
          :variants="groupedVariants.regular"
          :primary="variant"
          :related="groupedVariants.related"
          :class="classes.card({ index: i % 3 as (0 | 1 | 2) })"
          class="grid grid-rows-subgrid"
          data-container
        >
          <PacketVariantCardDiscountIcon />
          <PacketVariantCardTitleSection :class="classes.titleSection()" />
          <div data-content :class="classes.content()">
            <PacketVariantCardDescription />
            <PacketVariantCardActionsSection :class="classes.actionsSection()" data-actions>
              <PacketVariantCardAction
                v-if="variant.fixedAmount"
                :variant="variant"
                data-primary-action
              />
              <PacketAddWidget
                v-else
                :variant="variant"
                card-class="py-legacy-m"
                class="flex size-full flex-col justify-center"
              >
                <PacketQuantityInput
                  v-if="!isConsumer(auth.user)"
                  :variant="variant"
                  class="border-b border-gray-20 pb-legacy-m"
                />
              </PacketAddWidget>
              <PacketVariantCardRelated data-related />
            </PacketVariantCardActionsSection>
          </div>
        </PacketVariantCardContainer>
      </li>
      <li v-if="groupedVariants.pickAndMatch.length > 0" class="z-0">
        <PacketPickAndMatch
          :variants="groupedVariants.pickAndMatch"
          :related="groupedVariants.related"
        />
      </li>
    </ul>
  </UContainer>
</template>

<style scoped>
@reference "~/assets/styles/main.css";

[data-cards] {
  grid-template-columns: repeat(auto-fit, minmax(min(var(--preferred-col-width, 375px), 100%), 1fr));
  grid-template-rows: repeat(3, auto);
}

[data-container] {
  @apply row-span-3;

  [data-content] {
    @apply row-span-2;
  }

  &:has([data-related]) {
    @apply row-span-4;

    [data-content] {
      @apply row-span-3;
    }

    [data-actions] {
      @apply grid grid-rows-subgrid row-span-2;
    }

    [data-primary-action] {
      @apply border-b border-gray-20 pb-s;
    }
  }
}
</style>
